import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationRoute } from '@superchat/shared/notifications';

/**
 * Takes the agent to whatever a clicked notification was about.
 *
 * The service worker can focus the tab, but it cannot route inside it — a
 * focused tab still shows whatever was on screen when the agent walked away.
 * `sw.js` therefore posts the destination and this listens for it. Without
 * this half, clicking a push notification with the console already open did
 * nothing at all: the window came forward and the conversation the banner was
 * about was never opened.
 *
 * Mounted above the routes rather than inside a view, because the tab being
 * focused may be sitting anywhere — including on a view that has nothing to do
 * with chat.
 *
 * Navigation carries the app as well as the conversation and `ChatView` does
 * the rest: it switches tenant first, then resolves the thread. That is the
 * same path an emailed link takes, so there is one deep-link behaviour to keep
 * working rather than two.
 */
export function NotificationOpener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const onMessage = (event) => {
      if (event.data?.type !== 'superchat:open') return;
      // `route` comes from the worker; the payload is the fallback for a
      // worker left over from a previous deploy, which is a real case —
      // an old service worker can outlive the page that registered it.
      const route = event.data.route ?? notificationRoute(event.data.payload ?? {});
      navigate(route);
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  return null;
}

export default NotificationOpener;
